import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { StagePill, SectionHeader } from "@/components/common/Widgets";
import { fmtDate } from "@/lib/format";
import { Plus, Search, CheckCircle2, AlertCircle, XCircle } from "lucide-react";

const STAGES = ["DRAFT", "KYC", "FIELD_VERIFICATION", "FINANCIALS", "CMA", "CRA", "ASSESSMENT", "SECURITY", "APPROVAL", "SANCTIONED", "REJECTED"];
const LOAN_TYPES = ["PERSONAL_LOAN", "HOUSING_LOAN", "GOLD_LOAN", "TW_LOAN", "FW_LOAN", "CC", "TL", "MUDRA_SHISHU", "MUDRA_KISHORE", "MUDRA_TARUN", "SME_CC", "STAND_UP_INDIA", "WC_NAYAK", "PROJECT", "CGTMSE_HYBRID"];

const VERDICT_BADGE = {
  APPROVED: { cls: "bg-success/15 text-success border-success/40", icon: CheckCircle2, label: "Eligible" },
  REFER:    { cls: "bg-warning/15 text-warning border-warning/40", icon: AlertCircle, label: "Refer" },
  REJECTED: { cls: "bg-destructive/15 text-destructive border-destructive/40", icon: XCircle, label: "Not Eligible" },
};

function EligibilityCell({ snap }) {
  if (!snap) return <span className="text-xs text-muted-foreground">—</span>;
  const m = VERDICT_BADGE[snap.verdict] || VERDICT_BADGE.REFER;
  const Icon = m.icon;
  const cap = snap.input_summary?.max_foir_pct ?? 50;
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge variant="outline" className={`text-[10px] gap-1 cursor-help ${m.cls}`} data-testid={`elig-badge-${snap.verdict}`}>
            <Icon className="h-3 w-3" />
            <span>{m.label}</span>
            <span className="opacity-70 mono">· FOIR {snap.current_foir_pct}%</span>
          </Badge>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs text-xs">
          <div className="font-semibold mb-1">FOIR-based Eligibility</div>
          <div>Verdict: <span className="font-medium">{snap.verdict}</span></div>
          <div>FOIR: {snap.current_foir_pct}% (cap {cap}%)</div>
          <div>Max Eligible: ₹{snap.max_eligible_amount_lakhs}L</div>
          {snap.proposed_emi > 0 && <div>Proposed EMI: ₹{Number(snap.proposed_emi).toLocaleString("en-IN")}</div>}
          <div className="text-muted-foreground mt-1">Source: <span className="mono">{snap.source}</span></div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export default function ApplicationsPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [stage, setStage] = useState("all");
  const [type, setType] = useState("all");
  const [eligFilter, setEligFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = { q: q || undefined, stage: stage !== "all" ? stage : undefined, loan_type: type !== "all" ? type : undefined };
      const res = await api.get("/applications", { params });
      setItems(res.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [stage, type]);

  const visibleItems = eligFilter === "all"
    ? items
    : eligFilter === "none"
      ? items.filter((a) => !a.eligibility_snapshot)
      : items.filter((a) => a.eligibility_snapshot?.verdict === eligFilter);

  return (
    <div className="space-y-4">
      <SectionHeader title="Loan Applications" subtitle="All open and closed applications" action={
        <Link to="/applications/new"><Button data-testid="applications-new-button"><Plus className="h-4 w-4 mr-1.5" />New Application</Button></Link>
      } />
      <Card className="p-3 flex flex-col md:flex-row gap-2 md:items-center">
        <form onSubmit={(e) => { e.preventDefault(); fetchData(); }} className="relative flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search ref no, customer…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="applications-search-input" />
        </form>
        <div className="flex gap-2 flex-wrap">
          <Select value={stage} onValueChange={setStage}>
            <SelectTrigger className="w-[180px]" data-testid="applications-stage-filter"><SelectValue placeholder="Stage" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All stages</SelectItem>
              {STAGES.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, ' ')}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger className="w-[170px]" data-testid="applications-type-filter"><SelectValue placeholder="Type" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              {LOAN_TYPES.map((t) => <SelectItem key={t} value={t}>{t.replace(/_/g, ' ')}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={eligFilter} onValueChange={setEligFilter}>
            <SelectTrigger className="w-[170px]" data-testid="applications-eligibility-filter"><SelectValue placeholder="Eligibility" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All eligibility</SelectItem>
              <SelectItem value="APPROVED">Eligible</SelectItem>
              <SelectItem value="REFER">Refer</SelectItem>
              <SelectItem value="REJECTED">Not Eligible</SelectItem>
              <SelectItem value="none">No snapshot</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="secondary" onClick={fetchData}>Refresh</Button>
        </div>
      </Card>

      <Card className="p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="text-left px-3 py-2.5">Ref</th>
                <th className="text-left px-3 py-2.5">Customer</th>
                <th className="text-left px-3 py-2.5">CIF</th>
                <th className="text-left px-3 py-2.5">Type</th>
                <th className="text-right px-3 py-2.5">CC (₹L)</th>
                <th className="text-right px-3 py-2.5">TL (₹L)</th>
                <th className="text-left px-3 py-2.5">Eligibility</th>
                <th className="text-left px-3 py-2.5">Stage</th>
                <th className="text-left px-3 py-2.5">Branch</th>
                <th className="text-left px-3 py-2.5">Updated</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={10} className="text-center py-8 text-muted-foreground">Loading…</td></tr>}
              {!loading && visibleItems.length === 0 && (
                <tr><td colSpan={10} className="text-center py-12 text-sm text-muted-foreground">No applications match your filters.</td></tr>
              )}
              {visibleItems.map((a) => (
                <tr key={a.id} className="border-t border-border hover:bg-muted/30 cursor-pointer" onClick={() => navigate(`/applications/${a.id}`)} data-testid={`applications-row-${a.id}`}>
                  <td className="px-3 py-2 mono text-xs">{a.ref_no}</td>
                  <td className="px-3 py-2 font-medium">{a.customer_name}</td>
                  <td className="px-3 py-2 mono text-xs">{a.customer_cif}</td>
                  <td className="px-3 py-2 text-xs">{a.loan_type}</td>
                  <td className="px-3 py-2 num-right">{a.cc_proposed ?? a.personal_loan_amount ?? '—'}</td>
                  <td className="px-3 py-2 num-right">{a.tl_proposed ?? a.housing_loan_amount ?? '—'}</td>
                  <td className="px-3 py-2"><EligibilityCell snap={a.eligibility_snapshot} /></td>
                  <td className="px-3 py-2"><StagePill stage={a.stage} /></td>
                  <td className="px-3 py-2 text-xs">{a.branch || "—"}</td>
                  <td className="px-3 py-2 text-xs text-muted-foreground mono">{fmtDate(a.updated_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
